import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Calendar, Mail, Star, Send, Check, ChevronDown, ChevronUp, FlaskConical, ChevronLeft, ChevronRight } from 'lucide-react';
import MailjetHistorySection from '../../components/admin/MailjetHistorySection';

const WEEKDAY_KR = ['일', '월', '화', '수', '목', '금', '토'];

function todayKst() {
  const kst = new Date(Date.now() + 9 * 60 * 60 * 1000);
  return kst.toISOString().slice(0, 10);
}

function formatDateKr(dateStr) {
  const [y, m, d] = dateStr.split('-').map(Number);
  const w = WEEKDAY_KR[new Date(y, m - 1, d).getDay()];
  return `${m}월 ${d}일 (${w})`;
}

const DEFAULT_FROM = 'p!nk 김태연 <master@impink.co.kr>';
const DEFAULT_TO = 'pink@impink.co.kr';
const DEFAULT_BCC = [
  'genie-promotion@kt.com', 'dl_bugs_newalbum@bugs.co.kr', 'curation@ygmail.net',
  'seungbum.lim@navercorp.com', 'kwangjeong.kim@bemyfriends.com', 'promotion@kakaoent.com',
  'mlb.music@kakaoent.com', 'melon.music@knworks.co.kr', 'melon.mv@knworks.co.kr',
  'umag_notice@umag.io', 'drcho@umag.io', 'doo@umag.io', 'music.promotion@dreamus.io',
].join(', ');

export default function AdminPromotionEmailPage() {
  const [date, setDate] = useState(todayKst());

  // 발송정보
  const [sendFrom, setSendFrom] = useState(DEFAULT_FROM);
  const [sendTo, setSendTo] = useState(DEFAULT_TO);
  const [sendBcc, setSendBcc] = useState(DEFAULT_BCC);
  const [sendInfoOpen, setSendInfoOpen] = useState(false);

  // 테스트 발송
  const [testEmail, setTestEmail] = useState('');

  const [preview, setPreview] = useState(null);
  const [subject, setSubject] = useState('');
  const [htmlBody, setHtmlBody] = useState('');
  const [previewMode, setPreviewMode] = useState('rendered');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [testSending, setTestSending] = useState(false);
  const [testSent, setTestSent] = useState(false);

  // 발송 이력
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [statusData, setStatusData] = useState({});

  const fetchLogs = async () => {
    setLogsLoading(true);
    try {
      const res = await axios.get('/api/promotion-email/logs?type=daily&limit=10');
      setLogs(res.data);
    } catch {}
    setLogsLoading(false);
  };

  const fetchStatus = async (logId) => {
    setStatusData(prev => ({ ...prev, [logId]: { loading: true } }));
    try {
      const res = await axios.get(`/api/promotion-email/logs/${logId}/status`);
      setStatusData(prev => ({ ...prev, [logId]: { data: res.data } }));
    } catch {
      setStatusData(prev => ({ ...prev, [logId]: { error: '조회 실패' } }));
    }
  };
  useEffect(() => { fetchLogs(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // 날짜 바뀌면 자동 초안 생성 (releaseTime은 앨범 데이터에서 자동 수신)
  useEffect(() => {
    if (!date) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    setSent(false);
    setTestSent(false);

    axios.post('/api/promotion-email/preview', { date })
      .then((res) => {
        if (cancelled) return;
        setPreview(res.data);
        setSubject(res.data.subject);
        setHtmlBody(res.data.html);
      })
      .catch((err) => {
        if (cancelled) return;
        setPreview(null);
        setError(err.response?.data?.message || '초안 생성 중 오류가 발생했습니다.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);


  const handleSend = async () => {
    if (!subject.trim() || !htmlBody.trim()) return;
    const bccList = sendBcc.split(',').map(s => s.trim()).filter(Boolean);
    if (!window.confirm(`${bccList.length}개 플랫폼 담당자에게 발송하시겠습니까?`)) return;
    setSending(true);
    try {
      await axios.post('/api/promotion-email/send', {
        subject, html: htmlBody,
        from: sendFrom, to: sendTo, bcc: sendBcc,
      });
      setSent(true);
      fetchLogs();
    } catch (err) {
      alert(err.response?.data?.message || '발송에 실패했습니다.');
    }
    setSending(false);
  };

  const handleTestSend = async () => {
    if (!testEmail.trim()) return alert('테스트 수신 이메일을 입력하세요.');
    if (!subject.trim() || !htmlBody.trim()) return;
    setTestSending(true);
    try {
      await axios.post('/api/promotion-email/send', {
        subject: `[테스트] ${subject}`,
        html: htmlBody,
        from: sendFrom,
        to: testEmail.trim(),
        bcc: '',
      });
      setTestSent(true);
      setTimeout(() => setTestSent(false), 3000);
    } catch (err) {
      alert(err.response?.data?.message || '테스트 발송에 실패했습니다.');
    }
    setTestSending(false);
  };

  const bccCount = sendBcc.split(',').filter(s => s.trim()).length;

  const moveDate = (days) => {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    setDate(d.toISOString().slice(0, 10));
  };

  return (
    <div className="space-y-6">
      <div className="flex mb-8 justify-center items-center">
        <h1 className="text-xl sm:text-2xl font-bold text-white tracking-tight">프로모션 이메일</h1>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[310px_1fr] gap-5">
        {/* 왼쪽: 설정 */}
        <div className="space-y-4">
          {/* 날짜 */}
          <div className="bg-[#111827] border border-white/[0.08] rounded-2xl p-5">
            <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">발매일</label>
            <div className="flex items-center gap-2">
              <button
                onClick={() => moveDate(-1)}
                className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/[0.08] transition-colors shrink-0"
              >
                <ChevronLeft size={16} />
              </button>
              <div className="relative flex-1">
                <Calendar size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="w-full bg-white/[0.05] border border-white/[0.08] rounded-lg pl-8 pr-3 py-2.5 text-sm text-white focus:outline-none focus:border-amber-500/50 [color-scheme:dark]"
                />
              </div>
              <button
                onClick={() => moveDate(1)}
                className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/[0.08] transition-colors shrink-0"
              >
                <ChevronRight size={16} />
              </button>
            </div>
            <p className="text-xs text-slate-600 mt-2">발매시간은 프로모션 관리에서 앨범별로 설정합니다.</p>
          </div>

          {/* 발송 정보 (수정 가능) */}
          <div className="bg-[#111827] border border-white/[0.08] rounded-2xl overflow-hidden">
            <button
              onClick={() => setSendInfoOpen(o => !o)}
              className="w-full flex items-center justify-between px-5 py-4 hover:bg-white/[0.02] transition-colors"
            >
              <div className="flex items-center gap-2">
                <Mail size={14} className="text-amber-400" />
                <span className="text-white text-sm font-bold">발송 정보</span>
                <span className="text-xs text-slate-500">BCC {bccCount}명</span>
              </div>
              {sendInfoOpen ? <ChevronUp size={16} className="text-slate-500" /> : <ChevronDown size={16} className="text-slate-500" />}
            </button>

            {sendInfoOpen && (
              <div className="px-5 pb-5 space-y-3 border-t border-white/[0.06] pt-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">From</label>
                  <input
                    type="text"
                    value={sendFrom}
                    onChange={(e) => setSendFrom(e.target.value)}
                    className="w-full bg-white/[0.05] border border-white/[0.08] rounded-lg px-3 py-2 text-xs text-white focus:outline-none focus:border-amber-500/50"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">To</label>
                  <input
                    type="text"
                    value={sendTo}
                    onChange={(e) => setSendTo(e.target.value)}
                    className="w-full bg-white/[0.05] border border-white/[0.08] rounded-lg px-3 py-2 text-xs text-white focus:outline-none focus:border-amber-500/50"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-1">BCC</label>
                  <textarea
                    value={sendBcc}
                    onChange={(e) => setSendBcc(e.target.value)}
                    rows={5}
                    className="w-full bg-white/[0.05] border border-white/[0.08] rounded-lg px-3 py-2 text-xs text-white resize-none focus:outline-none focus:border-amber-500/50"
                  />
                </div>
              </div>
            )}
          </div>

          {/* 테스트 발송 */}
          {preview && (
            <div className="bg-[#111827] border border-white/[0.08] rounded-2xl p-4 space-y-3">
              <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wide">테스트 발송</label>
              <div className="flex gap-2">
                <input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="수신 이메일 주소"
                  className="flex-1 bg-white/[0.05] border border-white/[0.08] rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-amber-500/50"
                />
                <button
                  onClick={handleTestSend}
                  disabled={testSending || !testEmail.trim()}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors shrink-0 ${
                    testSent
                      ? 'bg-emerald-500/20 text-emerald-400'
                      : 'bg-white/[0.08] text-slate-300 hover:bg-white/[0.12]'
                  }`}
                >
                  {testSent ? <Check size={14} /> : <FlaskConical size={14} />}
                  {testSent ? '발송됨' : testSending ? '...' : '테스트'}
                </button>
              </div>
              <p className="text-xs text-slate-600">제목 앞에 [테스트]가 붙고 BCC 없이 발송됩니다.</p>
            </div>
          )}

          {/* 실제 발송 버튼 */}
          {preview && (
            <button
              onClick={handleSend}
              disabled={sending || sent}
              className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-sm transition-colors ${
                sent
                  ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                  : 'bg-amber-500 text-black hover:bg-amber-400'
              }`}
            >
              {sent ? <Check size={18} /> : <Send size={18} />}
              {sent ? '발송 완료' : sending ? '발송 중...' : `${bccCount}개 플랫폼에 발송`}
            </button>
          )}
        </div>

        {/* 오른쪽: 이메일 편집 */}
        <div className="space-y-4">
          {loading ? (
            <div className="bg-[#111827] border border-white/[0.08] rounded-2xl flex items-center justify-center" style={{ minHeight: 480 }}>
              <div className="text-center">
                <div className="w-6 h-6 border-2 border-amber-400 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                <p className="text-sm text-slate-500">초안 생성 중...</p>
              </div>
            </div>
          ) : error ? (
            <div className="bg-[#111827] border border-white/[0.08] rounded-2xl flex items-center justify-center" style={{ minHeight: 480 }}>
              <div className="text-center">
                <Star size={36} className="mx-auto mb-3 text-slate-700" />
                <p className="text-sm text-slate-400">{error}</p>
                <p className="text-xs text-slate-600 mt-2">
                  {date && `프로모션 관리에서 ${formatDateKr(date)} 발매 앨범에 프로모션을 설정하세요.`}
                </p>
              </div>
            </div>
          ) : preview ? (
            <>
              {/* 제목 */}
              <div className="bg-[#111827] border border-white/[0.08] rounded-2xl p-4">
                <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">이메일 제목</label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="w-full bg-white/[0.05] border border-white/[0.08] rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-amber-500/50"
                />
              </div>

              {/* 본문 */}
              <div className="bg-[#111827] border border-white/[0.08] rounded-2xl overflow-hidden">
                <div className="px-4 py-3 border-b border-white/[0.06] flex items-center justify-between">
                  <span className="text-white text-sm font-bold">이메일 본문</span>
                  <div className="flex gap-1">
                    {[['rendered', '미리보기'], ['html', 'HTML 편집']].map(([mode, label]) => (
                      <button
                        key={mode}
                        onClick={() => setPreviewMode(mode)}
                        className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
                          previewMode === mode ? 'bg-amber-500/20 text-amber-400' : 'text-slate-400 hover:text-white'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                {previewMode === 'rendered' ? (
                  <iframe
                    srcDoc={htmlBody}
                    title="email-preview"
                    className="w-full bg-white"
                    style={{ height: 'min(660px, 70vh)', border: 'none' }}
                  />
                ) : (
                  <textarea
                    value={htmlBody}
                    onChange={(e) => setHtmlBody(e.target.value)}
                    rows={30}
                    className="w-full bg-[#0a0f1a] text-slate-300 text-xs font-mono p-4 focus:outline-none resize-none"
                    spellCheck={false}
                  />
                )}
              </div>
            </>
          ) : null}
        </div>
      </div>

      {/* 최근 발송 이력 */}
      <EmailLogSection
        logs={logs}
        logsLoading={logsLoading}
        statusData={statusData}
        onRefresh={fetchLogs}
        onFetchStatus={fetchStatus}
      />

      {/* 기간별 발송 조회 (Mailjet 직접) */}
      <MailjetHistorySection
        baseUrl="/api/promotion-email/mailjet-history?type=daily"
        accent="amber"
      />
    </div>
  );
}

function EmailLogSection({ logs, logsLoading, statusData, onRefresh, onFetchStatus }) {
  return (
    <div className="bg-[#111827] border border-white/[0.08] rounded-2xl p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-white text-sm font-bold">최근 발송 이력</h3>
        <button
          onClick={onRefresh}
          className="text-xs text-slate-400 hover:text-white transition-colors"
        >
          새로고침
        </button>
      </div>

      {logsLoading ? (
        <p className="text-xs text-slate-500 text-center py-4">불러오는 중...</p>
      ) : logs.length === 0 ? (
        <p className="text-xs text-slate-600 text-center py-4">발송 이력이 없습니다.</p>
      ) : (
        <div className="space-y-3">
          {logs.map(log => {
            const sd = statusData[log.id];
            return (
              <div key={log.id} className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-white text-xs font-medium truncate">{log.subject}</p>
                    <p className="text-slate-500 text-xs mt-0.5">
                      {new Date(log.sentAt).toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' })}
                      <span className="ml-2 text-slate-600">{log.recipientCount}명 발송</span>
                    </p>
                  </div>
                  <button
                    onClick={() => onFetchStatus(log.id)}
                    disabled={sd?.loading}
                    className="shrink-0 text-xs px-2.5 py-1 rounded-lg bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 transition-colors disabled:opacity-50"
                  >
                    {sd?.loading ? '조회중...' : '상태 확인'}
                  </button>
                </div>

                {sd?.error && (
                  <p className="text-xs text-red-400 mt-2">{sd.error}</p>
                )}

                {sd?.data && (
                  <div className="mt-3 border-t border-white/[0.06] pt-3">
                    <div className="flex gap-4 mb-2 text-xs text-slate-500">
                      <span>수신완료 <span className="text-green-400 font-medium">{sd.data.deliveredCount}</span></span>
                      <span>반송 <span className="text-red-400 font-medium">{sd.data.bounceCount}</span></span>
                      <span>전체 <span className="text-white font-medium">{sd.data.totalCount}</span></span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                      {sd.data.results.map((r, i) => (
                        <div key={i} className="flex items-center justify-between text-xs px-2 py-1 rounded-lg bg-white/[0.02]">
                          <span className="text-slate-400 truncate mr-2">{r.email}</span>
                          <span className={`shrink-0 font-medium ${r.color || 'text-gray-400'}`}>{r.label || r.status}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
